export type EffectType = 'rain' | 'haze' | 'clouds' | 'warmLight';
export type TaskSurfaceStyle = 'sticky' | 'notebook' | 'slate' | 'paper';
export type TimerStyle = 'digital' | 'analog' | 'minimal' | 'embedded';
export type TimerBlend = 'glow' | 'reflection' | 'etched' | 'floating';

export interface World {
  id: string;
  name: string;
  description: string;
  thumbnail: string;
  video1080: string;
  video720: string;
  videoPoster?: string; // First frame for loading
  effectType: EffectType;
  timerAnchor: {
    x: string;
    y: string;
    transform?: string;
    style: TimerStyle;
    blend: TimerBlend; // How timer integrates with scene
    glowColor?: string; // For neon/reflection effects
  };
  taskSurface: {
    x: string;
    y: string;
    style: TaskSurfaceStyle;
  };
  ambienceSrc?: string;
  colorScheme: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
}

// Video sources from Pexels (free, no attribution required)
// Download these and place in /public/assets/worlds/ or use CDN URLs
//
// RECOMMENDED PEXELS VIDEOS:
// Rain:     https://www.pexels.com/video/rain-over-window-13107321/ (by Yahia Alibrahim)
//           https://www.pexels.com/video/rainy-night-drive-with-city-lights-reflection-30181179/
// Desert:   https://www.pexels.com/video/a-desert-with-sand-dunes-and-some-bushes-19376556/
//           https://www.pexels.com/video/vertical-shot-of-desert-dunes-11371498/
// Mountain: https://www.pexels.com/video/clouds-on-a-mountain-peak-2638063/ (by Luis Quintero)
//           https://www.pexels.com/video/time-lapse-of-moving-clouds-over-a-mountain-area-16754231/
// Cabin:    https://www.pexels.com/video/a-fireplace-inside-a-wooden-cabin-3785183/ (by Taryn Elliott)
//           https://www.pexels.com/video/firewood-fire-fireplace-cosy-4231468/

// For now, using fireplace video for all scenes until scene selector is fixed
const FIREPLACE_VIDEO = '/assets/worlds/fireplace.mp4';

export const worlds: World[] = [
  {
    id: 'cabin',
    name: 'Night Café',
    description: 'A snowy late-night café, working alongside others',
    thumbnail: '/assets/worlds/cabin-thumb.jpg',
    video1080: FIREPLACE_VIDEO,
    video720: FIREPLACE_VIDEO,
    effectType: 'warmLight',
    timerAnchor: {
      x: '50%',
      y: '38%',
      style: 'analog',
      blend: 'reflection',
      glowColor: '#f0c890',
    },
    taskSurface: {
      x: '50%',
      y: '70%',
      style: 'slate',
    },
    ambienceSrc: '/assets/worlds/cabin-ambience.mp3',
    colorScheme: {
      primary: '#d4a066',
      secondary: '#241c17',
      accent: '#f0c890',
      background: '#120e0a',
    },
  },
];

export const getWorld = (id: string): World | undefined => {
  return worlds.find((w) => w.id === id);
};

export const defaultWorld = worlds[0];

// YouTube music mode mapping (preserved from original)
export const modeToYoutubeId: Record<string, string> = {
  lofi: 'rFZHOHl-L8A',
  study: 'rFZHOHl-L8A',
  focus: 'mg7netw1JuM',
  white: 'LD92VXIl8hc',
  forest: 'GwIO1nzuJrw',
  silent: 'kvO_nHnvPtQ',
};

export type MusicMode = keyof typeof modeToYoutubeId | 'youtube';
